import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Header, ReportHeader, CardContainer, NavbarWrapper } from './styles'
import Text from 'particles/Text'
import { useNewTranslation } from 'hooks/index'
import { CampaignState } from 'state/campaigns'
import Tab, { TabOption } from 'particles/Tab'
import { parseBackendDate } from 'services/dates'
import { CAMPAIGN_REPORT_ENUM, selectSelectedCampaignReport, setSelectedCampaignReport } from 'state/review'
import Overview from './Tabs/Overview'
import TaskPerformance from './Tabs/TaskPerformance'
import IndividualPerformance from './Tabs/IndividualPerformance'
import TopPerformers from './Tabs/TopPerformers'
import { selectTasks } from 'state/tasks'
import _ from 'lodash'
import { PLATFORM_ENUM } from 'services/api/constants'
import { STATE_KEYS } from 'state/requests'
import { selectRequestsAreLoading } from 'state'
import Loading from 'components/Loading'
import RectangleButton from 'particles/Button/RectangleButton'
import Modal from 'particles/Modal'
import { downloadCampaignReport } from 'state/campaignReport'

interface NavBarProps {
    className?: string
    selectedTab: string
}

const NavBar: React.FC<NavBarProps> = ({ className, selectedTab }) => {
    const [t] = useNewTranslation()
    const dispatch = useDispatch()

    const tasks = useSelector(selectTasks)
    const campaignPlatforms = useMemo(() => {
        return _.uniq(tasks.map(task => task.platform))
    }, [tasks])

    const hasNoEngagement =
        campaignPlatforms.length === 0 ||
        (campaignPlatforms.length === 1 && campaignPlatforms[0] === PLATFORM_ENUM.OTHER)

    const tabOptions: TabOption[] = [
        {
            id: 'overview',
            label: t('submission.campaignReport.overviewTab'),
            onClick: () => dispatch(setSelectedCampaignReport(CAMPAIGN_REPORT_ENUM.OVERVIEW)),
        },
        {
            id: 'taskPerformance',
            label: t('submission.campaignReport.campaignPerformance'),
            onClick: () => dispatch(setSelectedCampaignReport(CAMPAIGN_REPORT_ENUM.TASK_PERFORMANCE)),
        },
        {
            id: 'individualPerformance',
            label: t('submission.campaignReport.individualPerformance'),
            onClick: () => dispatch(setSelectedCampaignReport(CAMPAIGN_REPORT_ENUM.INDIVIDUAL_PERFORMANCE)),
            isHidden: hasNoEngagement,
        },
        {
            id: 'topPerformers',
            label: t('submission.campaignReport.topPerformers'),
            onClick: () => dispatch(setSelectedCampaignReport(CAMPAIGN_REPORT_ENUM.TOP_PERFORMERS)),
            isHidden: hasNoEngagement,
        },
    ]

    return (
        <div className={className}>
            <NavbarWrapper>
                <Tab
                    tabItemAlign="left"
                    tabs={tabOptions}
                    selectedTab={selectedTab}
                    form="main"
                    isDisabledClickable={false}
                    className="tab"
                />
            </NavbarWrapper>
        </div>
    )
}

interface ReportProps {
    campaign: CampaignState
}

const Report: React.FC<ReportProps> = ({ campaign }) => {
    const [t] = useNewTranslation()
    const dispatch = useDispatch()
    const isLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.CampaignSummary, STATE_KEYS.TasksList]))
    const selectedCampaignReport = useSelector(selectSelectedCampaignReport)

    const [showDownloadReportModal, setShowDownloadReportModal] = useState(false)

    const { brand, title, time_window, country } = campaign || {}
    const startDate = parseBackendDate(time_window?.start, country)
    const endDate = parseBackendDate(time_window?.end, country)

    const isOverviewPageMode = selectedCampaignReport === CAMPAIGN_REPORT_ENUM.OVERVIEW
    const isTaskPageMode = selectedCampaignReport === CAMPAIGN_REPORT_ENUM.TASK_PERFORMANCE
    const isIndividualPageMode = selectedCampaignReport === CAMPAIGN_REPORT_ENUM.INDIVIDUAL_PERFORMANCE
    const isTopPerformersPageMode = selectedCampaignReport === CAMPAIGN_REPORT_ENUM.TOP_PERFORMERS

    const handleDownloadReport = () => {
        dispatch(downloadCampaignReport({ uuid: campaign.uuid }))
        setShowDownloadReportModal(false)
    }

    if (!campaign) return null

    if (isLoading) return <Loading />

    return (
        <>
            <ReportHeader>
                <Header>
                    <div style={{ display: 'flex', gap: 12, flex: 1 }}>
                        <img className="brand-logo" src={brand?.logo_image?.image} />
                        <div className="campaign-info">
                            <Text scale="bodyM700">{title}</Text>
                            <Text scale="bodyM500" color="secondary">
                                {`${brand.name} | ${t('submission.campaignReport.campaignPeriodTitle')} ${startDate} - ${endDate}`}
                            </Text>
                        </div>
                    </div>
                    <RectangleButton
                        label={t('button.downloadReport')}
                        form="outlined"
                        size="m"
                        onClick={() => setShowDownloadReportModal(true)}
                    />
                </Header>
                <NavBar selectedTab={selectedCampaignReport as string} />
            </ReportHeader>
            <CardContainer>
                {isOverviewPageMode && <Overview campaign={campaign} />}
                {isTaskPageMode && <TaskPerformance campaign={campaign} />}
                {isIndividualPageMode && <IndividualPerformance campaign={campaign} />}
                {isTopPerformersPageMode && <TopPerformers campaign={campaign} />}
            </CardContainer>
            {showDownloadReportModal && (
                <Modal
                    title={t('downloadCampaignReportModal.title')}
                    subtext={t('downloadCampaignReportModal.subtext', { campaignName: campaign.title })}
                    size="m"
                    isClosable
                    hasButtonSet
                    hasIcon={false}
                    onConfirm={handleDownloadReport}
                    onClose={() => setShowDownloadReportModal(false)}
                    primaryButtonProps={{
                        label: t('button.download'),
                    }}
                    secondaryButtonProps={{
                        label: t('button.cancel'),
                    }}
                />
            )}
        </>
    )
}

export default Report
